import { Operation, UNIT_COST_CALCULATION } from "@forgepoint/shared";
import { formatCurrency } from "@/lib/utils";
import { useOrganizationStore } from "@/store/organizationStore";
import Decimal from "decimal.js";

interface OperationRowPriceProps {
  /** The operation whose cost is displayed. */
  operation: Operation;
  /** "row" stacks the basis under the amount, "trigger" keeps it on one line. */
  variant: "row" | "trigger";
}

/**
 * Renders an operation's cost with the basis it is measured in.
 * @param props - The operation and the layout variant
 * @returns The formatted cost and its basis suffix
 */
export function OperationRowPrice({
  operation,
  variant,
}: OperationRowPriceProps) {
  const baseCurrency = useOrganizationStore(
    (state) => state.organization?.baseCurrency,
  );
  const amount = formatCurrency(operationDisplayRate(operation), baseCurrency);
  const basis = operationCostBasis(operation);

  if (variant === "trigger") {
    return (
      <span className="tabular-nums">
        {amount}
        <span className="text-muted-foreground">{basis}</span>
      </span>
    );
  }

  return (
    <div className="flex flex-col items-end leading-tight tabular-nums">
      <span>{amount}</span>
      <span className="text-xs text-muted-foreground">{basis}</span>
    </div>
  );
}

/**
 * Resolves the unit an operation's cost is measured in.
 * @param operation - The operation whose costing method is inspected
 * @returns "/hr" for time-based costing and "/unit" for per-unit costing
 */
function operationCostBasis(operation: Operation): string {
  return operation.unitCostCalculation === UNIT_COST_CALCULATION.PER_UNIT
    ? "/unit"
    : "/hr";
}

function operationDisplayRate(operation: Operation): string {
  if (operation.unitCostCalculation === UNIT_COST_CALCULATION.PER_UNIT) {
    return operation.cost;
  }
  return new Decimal(operation.cost).plus(operation.burdenRate ?? 0).toString();
}
